import React from "react";
import { AlertTriangle, Check } from "lucide-react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { ToolIcon } from "../../../components/classic/panels";
import { CATS, useCatColors, fmtDur, fmtClock } from "../bits";
import { Donut } from "../Donut";
import { Sk } from "./storyBits";

/* ── Loading skeletons, shaped like the charts they stand in for ──────────
   Each mirrors its component's real dimensions so nothing jumps when the
   payload lands. All of them ride the same .st-sk shimmer the hero uses. */

/** Stands in for DayVerdict — same band, same paddings. */
export function DayVerdictSk() {
  const t = useDashTheme();
  return (
    <div aria-hidden="true" style={{
      display: "flex", gap: 13, alignItems: "flex-start", padding: "15px 17px",
      borderRadius: t.r.card, marginBottom: t.s[4],
      background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: t.shadow.sm,
    }}>
      <Sk w={11} h={11} r={999} style={{ marginTop: 5, flex: "none" }} />
      <span style={{ flex: 1 }}>
        <Sk w="42%" h={14} style={{ marginBottom: 8 }} />
        <Sk w="68%" h={12} />
      </span>
      <Sk w={96} h={26} r={99} style={{ flex: "none" }} />
    </div>
  );
}

/** Stands in for DayClock — the 240px dial with its caption ticks. */
export function DayClockSk() {
  return (
    <div aria-hidden="true">
      <Sk w={240} h={240} r="50%" style={{ margin: "0 auto 14px" }} />
      <div style={{ display: "flex", gap: 14, justifyContent: "center" }}>
        <Sk w={64} h={11} /><Sk w={64} h={11} /><Sk w={64} h={11} />
      </div>
    </div>
  );
}

/** Stands in for WhatWentInto — ring plus ranked rows. */
export function WhatWentIntoSk() {
  return (
    <div aria-hidden="true" style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
      <Sk w={192} h={192} r="50%" style={{ flex: "none" }} />
      <div style={{ flex: 1, minWidth: 160 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginTop: i ? 12 : 0 }}>
            <Sk w={10} h={10} r={3} style={{ flex: "none" }} />
            <Sk w={`${72 - i * 11}%`} h={12} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Stands in for DayLanes — five label + lane rows. */
export function DayLanesSk() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} style={{ display: "flex", gap: 14, alignItems: "center", marginTop: i ? 12 : 0 }}>
          <Sk w={92} h={12} style={{ flex: "none" }} />
          <Sk h={22} r={6} style={{ flex: 1 }} />
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   The shape of the day — three graphs and a verdict.

   Everything here is derived from /day. None of it costs an AI credit: the
   brief, the thread deep-read and the app detail are the metered calls, and
   they stay where they were. That matters for the verdict in particular —
   deciding whether a day is worth opening should never itself cost anything.

   Three different shapes on purpose. Three bar charts stacked read as one
   chart repeated; a dial, a ring and a set of lanes read as three answers.
   ══════════════════════════════════════════════════════════════════════════ */

const hourLabel = (h) => `${h % 12 || 12}${h < 12 ? "am" : "pm"}`;

/* ── Verdict ───────────────────────────────────────────────────────────────
   The first screen has to end the question, not start it. A row marked
   Settled is a row you can skip, and that is the whole value. Pure arithmetic
   over what /day already returns. */
export function threadVerdict(th) {
  const avg = th.minutes / Math.max(1, th.stretches || 1);
  const bigBreaks = (th.interruptions || []).filter((g) => g.gapMin >= 30);
  if ((th.stretches || 0) >= 10 && avg < 12) {
    return {
      level: "look",
      label: "Worth a look",
      why: `Picked up ${th.stretches} times, averaging ${Math.round(avg)} min each — never sat with it for long.`,
    };
  }
  if (bigBreaks.length >= 2) {
    return {
      level: "look",
      label: "Worth a look",
      why: `Broken off ${bigBreaks.length} times for ${bigBreaks.map((g) => `${g.gapMin} min`).join(" and ")}.`,
    };
  }
  if (th.minutes >= 60) {
    return {
      level: "ok",
      label: "Settled",
      why: `Ran in ${th.stretches || 1} sitting${(th.stretches || 1) === 1 ? "" : "s"}, longest stretches uninterrupted.`,
    };
  }
  return { level: "ok", label: "Minor", why: "Too short to read anything into." };
}

export function DayVerdict({ threads, onOpen }) {
  const t = useDashTheme();
  const rows = threads || [];
  if (!rows.length) return null;
  const looks = rows.filter((th) => threadVerdict(th).level === "look");
  const tone = looks.length ? t.status.warning : t.status.positive;
  return (
    <div
      role="status"
      style={{
        display: "flex", gap: 13, alignItems: "flex-start", padding: "15px 17px",
        borderRadius: t.r.card, marginBottom: t.s[4],
        background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: t.shadow.sm,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 11, height: 11, borderRadius: 999, flex: "none", marginTop: 5,
          background: tone, boxShadow: `0 0 0 4px ${tone}2e`,
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", color: t.c.text }}>
          {looks.length
            ? `${looks.length} thing${looks.length === 1 ? "" : "s"} worth opening`
            : "Nothing here needs a closer look"}
        </div>
        <div style={{ fontSize: 12.5, color: t.c.textTertiary, marginTop: 4, lineHeight: 1.5 }}>
          {looks.length ? (
            <>
              {looks.map((th, i) => (
                <React.Fragment key={th.key}>
                  {i > 0 && " · "}
                  <button
                    type="button"
                    className="tg-shape-link"
                    onClick={() => onOpen && onOpen(th)}
                    style={{
                      border: 0, background: "transparent", padding: 0, cursor: "pointer",
                      font: "inherit", color: t.c.primary, fontWeight: 600,
                    }}
                  >
                    {th.title}
                  </button>
                </React.Fragment>
              ))}
              {" — everything else ran clean."}
            </>
          ) : (
            "A full day, in long uninterrupted runs. You can close this."
          )}
        </div>
      </div>
    </div>
  );
}

/* ── ① When they worked, as a dial ─────────────────────────────────────────
   A day IS a clock, so this needs no legend. Wedge length is how much of that
   hour was worked; wedge colour is what it mostly went into. Untouched hours
   are still drawn, so the working day reads against the rest of the 24. */
export function DayClock({ blocks, bounds, totals }) {
  const t = useDashTheme();
  const C = useCatColors();

  const hours = React.useMemo(() => {
    if (!bounds?.startMs) return [];
    const per = {};
    for (const b of blocks || []) {
      const wall = Math.max(1, Number(b.endMs) - Number(b.startMs));
      const tracked = Math.max(0, Number(b.minutes) || 0) * 60000;
      const share = Math.min(1, tracked / wall);
      if (share <= 0) continue;
      for (let ms = b.startMs; ms < b.endMs; ms += 60000) {
        const h = new Date(ms).getHours();
        per[h] = per[h] || { min: 0, cat: {} };
        per[h].min += share;
        per[h].cat[b.cat] = (per[h].cat[b.cat] || 0) + share;
      }
    }
    return Object.keys(per).map(Number).map((h) => {
      const top = Object.entries(per[h].cat).sort((a, b) => b[1] - a[1])[0];
      return { h, min: Math.round(per[h].min), cat: top ? top[0] : null };
    });
  }, [blocks, bounds]);

  if (!hours.length) return null;

  const byHour = {}; hours.forEach((r) => { byHour[r.h] = r; });
  const worked = hours.filter((r) => r.min > 0);
  const peak = worked.reduce((a, b) => (b.min > a.min ? b : a), worked[0]);
  const quiet = worked.filter((r) => r.min < 30).sort((a, b) => a.min - b.min)[0];

  const W = 300, cx = 150, cy = 150, r0 = 52, rMax = 126;
  const wedge = (r1, a0, a1) => {
    const P = (r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    const [x0, y0] = P(r0, a0), [x1, y1] = P(r1, a0), [x2, y2] = P(r1, a1), [x3, y3] = P(r0, a1);
    return `M${x0.toFixed(1)} ${y0.toFixed(1)} L${x1.toFixed(1)} ${y1.toFixed(1)} A${r1} ${r1} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)} L${x3.toFixed(1)} ${y3.toFixed(1)} A${r0} ${r0} 0 0 0 ${x0.toFixed(1)} ${y0.toFixed(1)} Z`;
  };

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${W}`} width="100%" height={240} style={{ display: "block", margin: "0 auto", maxWidth: 258, overflow: "visible" }}>
        <circle cx={cx} cy={cy} r={rMax + 2} fill="none" stroke={t.c.border} />
        {Array.from({ length: 24 }, (_, h) => {
          const r = byHour[h];
          const a0 = (h / 24) * Math.PI * 2 - Math.PI / 2;
          const a1 = ((h + 1) / 24) * Math.PI * 2 - Math.PI / 2 - 0.014;
          if (!r || !r.min) return <path key={h} d={wedge(r0 + 7, a0, a1)} fill={t.c.surfaceMuted} />;
          const r1 = r0 + 10 + (r.min / 60) * (rMax - r0 - 10);
          const isPeak = r.h === peak?.h;
          const isQuiet = quiet && r.h === quiet.h;
          return (
            <path
              key={h}
              d={wedge(r1, a0, a1)}
              fill={C[r.cat] || C.browse}
              stroke={isQuiet ? t.status.warning : undefined}
              strokeWidth={isQuiet ? 2 : undefined}
              style={isPeak ? { filter: `drop-shadow(0 0 7px ${t.c.primary}8c)` } : undefined}
            >
              <title>{`${hourLabel(h)} — ${r.min} min, mostly ${CATS[r.cat]?.label || r.cat}`}</title>
            </path>
          );
        })}
        <circle cx={cx} cy={cy} r={r0 - 3} fill={t.c.surface} />
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="21" fontWeight="720" fill={t.c.text}>
          {fmtDur(totals?.trackedMin || 0)}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9.5" fontWeight="800" letterSpacing="1.1" fill={t.c.textTertiary}>
          AT THE DESK
        </text>
        {[[0, "12a"], [6, "6a"], [12, "12p"], [18, "6p"]].map(([h, lab]) => {
          const a = (h / 24) * Math.PI * 2 - Math.PI / 2, rr = rMax + 15;
          return (
            <text key={h} x={(cx + rr * Math.cos(a)).toFixed(1)} y={(cy + rr * Math.sin(a) + 4).toFixed(1)}
              textAnchor="middle" fontSize="11" fontWeight="650" fill={t.c.textTertiary}>{lab}</text>
          );
        })}
      </svg>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", justifyContent: "center", marginTop: 12, fontSize: 12.5, color: t.c.textTertiary }}>
        {[...new Set(worked.map((r) => r.cat))].filter(Boolean).map((c) => (
          <span key={c} style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
            <i style={{ width: 11, height: 11, borderRadius: 3, background: C[c] || C.browse, flex: "none" }} />
            {CATS[c]?.label || c}
          </span>
        ))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
          <i style={{ width: 11, height: 11, borderRadius: 3, background: t.c.surfaceMuted, flex: "none" }} />
          nothing tracked
        </span>
      </div>
    </div>
  );
}

/* ── ② What the hours went into ───────────────────────────────────────────
   Category donut (the graph) plus ranked rows so a kind of work can open
   its top application. */
export function WhatWentInto({ blocks, onOpenApp }) {
  const t = useDashTheme();
  const C = useCatColors();

  const { list, apps } = React.useMemo(() => {
    const by = {};
    const byApp = {};
    (blocks || []).forEach((b) => {
      if (b.cat === "idle") return;
      by[b.cat] = (by[b.cat] || 0) + (b.minutes || 0);
      if (b.app) byApp[b.app] = { name: b.app, cat: b.cat, minutes: (byApp[b.app]?.minutes || 0) + (b.minutes || 0) };
    });
    return {
      list: Object.entries(by).sort((a, b) => b[1] - a[1]),
      apps: Object.values(byApp),
    };
  }, [blocks]);
  if (!list.length) return null;

  const total = list.reduce((a, [, m]) => a + m, 0) || 1;

  return (
    <div>
      <Donut apps={apps} totals={{ trackedMin: total }} />

      <div style={{ height: 12 }} />

      <div style={{
        border: `1px solid ${t.c.border}`, borderRadius: t.r.md, overflow: "hidden",
        background: t.isDark ? t.c.surfaceMuted : "#F7F8FB",
      }}>
        {list.map(([c, m], i) => {
          const col = C[c] || C.browse;
          const pct = Math.round((m / total) * 100);
          const mine = (blocks || []).filter((b) => b.cat === c);
          const apps = Object.entries(mine.reduce((a, b) => {
            if (b.app) a[b.app] = (a[b.app] || 0) + (b.minutes || 0);
            return a;
          }, {})).sort((a, b) => b[1] - a[1]);
          const topApp = apps[0];
          const clickable = !!(topApp && onOpenApp);
          return (
            <button
              key={c}
              type="button"
              className={clickable ? "st-reset st-brief-note st-focusable" : "st-reset"}
              onClick={() => clickable && onOpenApp(topApp[0])}
              style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%",
                padding: "12px 14px", textAlign: "left", font: "inherit",
                background: "transparent", border: 0,
                borderTop: i ? `1px solid ${t.c.border}` : "none",
                cursor: clickable ? "pointer" : "default",
              }}
            >
              <ToolIcon name={topApp ? topApp[0] : ""} cat={c} size={32} />
              <span style={{ flex: "0 0 132px", minWidth: 96 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 650, color: t.c.text }}>
                  {CATS[c]?.label || c}
                </span>
                <span style={{
                  display: "block", fontSize: 12, color: t.c.textTertiary, marginTop: 2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{topApp ? topApp[0] : "—"}</span>
              </span>
              <span style={{
                flex: 1, height: 8, borderRadius: 99, overflow: "hidden",
                background: t.isDark ? t.c.surface : "#fff",
              }}>
                <i style={{
                  display: "block", height: "100%", borderRadius: 99, background: col,
                  width: `${Math.max(4, (m / list[0][1]) * 100)}%`,
                }} />
              </span>
              <span style={{
                flex: "none", width: 48, textAlign: "right", fontSize: 16,
                fontWeight: 720, letterSpacing: "-0.03em", color: col, fontVariantNumeric: "tabular-nums",
              }}>{pct}%</span>
              <span style={{
                flex: "none", width: 58, textAlign: "right", fontSize: 12.5,
                fontWeight: 650, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums",
              }}>{fmtDur(m)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── ③ The day in detail, as lanes ────────────────────────────────────────
   A calendar was tried here and the data killed it: these are not events, they
   are 5-minute samples alternating between two or three apps, and two thirds of
   them are under 10 minutes. At any sane row height that is a 4px stripe with
   no room for a label. Lanes state the app ONCE in the gutter, so a 5-minute
   block never has to introduce itself.

   Runs of the same app less than two minutes apart are joined. That removes the
   barcode without inventing time — the joined edges were already touching. */
const JOIN_MS = 2 * 60000;
const KEEP_LANES = 5;

export function DayLanes({ blocks, bounds, onOpenThread, longestMin }) {
  const t = useDashTheme();
  const C = useCatColors();

  const model = React.useMemo(() => {
    if (!bounds?.startMs || !(blocks || []).length) return null;
    const span = Math.max(1, bounds.endMs - bounds.startMs);
    const byApp = {};
    [...blocks].sort((a, b) => a.startMs - b.startMs)
      .forEach((b) => { (byApp[b.app] = byApp[b.app] || []).push(b); });

    const runsFor = (list) => {
      const out = []; let cur = null;
      list.forEach((b) => {
        const add = Number(b.minutes) > 0 ? Number(b.minutes) : 0;
        if (cur && b.startMs - cur.endMs <= JOIN_MS) {
          cur.endMs = Math.max(cur.endMs, b.endMs);
          cur.minutes += add;
        } else {
          if (cur) out.push(cur);
          cur = { startMs: b.startMs, endMs: b.endMs, minutes: add, cat: b.cat, app: b.app, threadKey: b.threadKey };
        }
      });
      if (cur) out.push(cur);
      return out;
    };

    const all = Object.entries(byApp).map(([app, list]) => ({
      app, cat: list[0].cat, threadKey: list[0].threadKey,
      min: list.reduce((a, b) => a + (b.minutes || 0), 0),
      runs: runsFor(list),
    })).sort((a, b) => b.min - a.min);

    // Ten lanes where six hold five minutes each reads as six empty rows.
    const head = all.slice(0, KEEP_LANES), tail = all.slice(KEEP_LANES);
    const lanes = head.slice();
    if (tail.length) {
      lanes.push({
        app: `${tail.length} other apps`, sub: tail.map((r) => r.app).join(", "),
        cat: tail[0].cat, threadKey: null, isTail: true,
        min: tail.reduce((a, r) => a + r.min, 0),
        runs: tail.flatMap((r) => r.runs).sort((a, b) => a.startMs - b.startMs),
      });
    }

    // Untracked stretches, so the day does not silently close its gaps.
    const sorted = [...blocks].sort((a, b) => a.startMs - b.startMs);
    const gaps = []; let cur = null;
    sorted.forEach((b) => {
      if (!cur) { cur = [b.startMs, b.endMs]; return; }
      if (b.startMs > cur[1]) { gaps.push([cur[1], b.startMs]); cur = [b.startMs, b.endMs]; }
      else cur[1] = Math.max(cur[1], b.endMs);
    });

    const marks = [];
    const h0 = new Date(bounds.startMs); h0.setMinutes(0, 0, 0);
    for (let ms = h0.getTime(); ms <= bounds.endMs; ms += 3600000) if (ms >= bounds.startMs) marks.push(ms);

    const sittings = lanes.reduce((a, l) => a + l.runs.length, 0);

    return {
      lanes, marks, sittings, span,
      gaps: gaps.filter((g) => g[1] - g[0] >= 4 * 60000),
      X: (ms) => ((ms - bounds.startMs) / span) * 100,
    };
  }, [blocks, bounds]);

  if (!model) return null;
  const { lanes, marks, gaps, sittings, X } = model;
  const longestShown = Number(longestMin) > 0 ? Math.round(Number(longestMin)) : 0;
  const GUTTER = 186;

  const cell = { flex: `0 0 ${GUTTER}px`, display: "flex", alignItems: "center", gap: 9, padding: "0 12px", borderRight: `1px solid ${t.c.border}`, background: t.c.surfaceMuted, minWidth: 0 };
  const cellCls = "tg-lane-gutter";

  return (
    <div>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 12, fontSize: 12, color: t.c.textTertiary }}>
        <span>First activity <b style={{ color: t.c.text, fontWeight: 600 }}>{fmtClock(bounds.startMs)}</b></span>
        <span>Last <b style={{ color: t.c.text, fontWeight: 600 }}>{fmtClock(bounds.endMs)}</b></span>
        {longestShown ? (
          <span>Longest run <b style={{ color: t.c.text, fontWeight: 600 }}>{fmtDur(longestShown)}</b></span>
        ) : null}
        <span><b style={{ color: t.c.text, fontWeight: 600 }}>{sittings}</b> sittings across <b style={{ color: t.c.text, fontWeight: 600 }}>{lanes.length}</b> rows</span>
      </div>

      <div style={{ border: `1px solid ${t.c.border}`, borderRadius: 12, overflow: "hidden", background: t.c.surface, position: "relative" }}>
        <div style={{ display: "flex", minHeight: 26, background: t.c.surfaceMuted, borderBottom: `1px solid ${t.c.border}` }}>
          <div style={{ ...cell, background: "transparent" }} />
          <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
            {marks.map((ms) => (
              <span key={ms} style={{ position: "absolute", top: 6, left: `${X(ms).toFixed(3)}%`, transform: "translateX(-50%)", fontSize: 10, color: t.c.textTertiary, whiteSpace: "nowrap" }}>
                {fmtClock(ms)}
              </span>
            ))}
          </div>
        </div>

        <div style={{ position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", display: "flex" }} aria-hidden>
            <div style={{ flex: `0 0 ${GUTTER}px` }} />
            <div style={{ position: "relative", flex: 1 }}>
              {marks.map((ms) => (
                <i key={ms} style={{ position: "absolute", top: 0, bottom: 0, width: 1, left: `${X(ms).toFixed(3)}%`, background: t.c.border }} />
              ))}
              {gaps.map((g) => {
                const w = ((g[1] - g[0]) / model.span) * 100;
                return (
                  <u key={g[0]} style={{
                    position: "absolute", top: 0, bottom: 0, textDecoration: "none",
                    left: `${X(g[0]).toFixed(3)}%`, width: `${w.toFixed(3)}%`, opacity: 0.6,
                    background: `repeating-linear-gradient(45deg, transparent 0 6px, ${t.c.border} 6px 7px)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {w > 7 && (
                      <em style={{ fontStyle: "normal", fontSize: 10, fontWeight: 600, color: t.c.textTertiary, background: t.c.surface, padding: "1px 7px", borderRadius: 5, whiteSpace: "nowrap" }}>
                        {fmtDur(Math.round((g[1] - g[0]) / 60000))} away
                      </em>
                    )}
                  </u>
                );
              })}
            </div>
          </div>

          {lanes.map((L) => (
            <div
              key={L.app}
              className={L.threadKey && onOpenThread ? "tg-lane-row" : undefined}
              role={L.threadKey && onOpenThread ? "button" : undefined}
              tabIndex={L.threadKey && onOpenThread ? 0 : undefined}
              onClick={() => L.threadKey && onOpenThread && onOpenThread(L.threadKey)}
              onKeyDown={(e) => {
                if (!L.threadKey || !onOpenThread) return;
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenThread(L.threadKey); }
              }}
              style={{
                display: "flex", alignItems: "stretch", minHeight: 40,
                borderTop: `1px solid ${t.c.border}`,
                cursor: L.threadKey && onOpenThread ? "pointer" : "default",
              }}
            >
              <div className={cellCls} style={cell}>
                <ToolIcon name={L.isTail ? "" : L.app} cat={L.cat} size={22} />
                <span style={{ fontSize: 12.5, fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.25, color: t.c.text }}>
                  {L.app}
                  <em style={{ display: "block", fontStyle: "normal", fontWeight: 400, color: t.c.textTertiary, fontSize: 10.5, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {L.isTail ? L.sub : (CATS[L.cat]?.label || L.cat)}
                  </em>
                </span>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: t.c.textTertiary, flex: "none", fontVariantNumeric: "tabular-nums" }}>
                  {fmtDur(L.min)}
                </span>
              </div>
              <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                {L.runs.map((r) => {
                  const w = ((r.endMs - r.startMs) / model.span) * 100;
                  const mins = Number(r.minutes) > 0 ? Math.round(r.minutes) : 0;
                  const markLongest = longestShown > 0 && mins === longestShown;
                  return (
                    <b
                      key={r.startMs}
                      title={`${L.isTail ? r.app : L.app} — ${fmtClock(r.startMs)} to ${fmtClock(r.endMs)} · ${fmtDur(mins)}`}
                      style={{
                        position: "absolute", top: 10, height: 20, borderRadius: 5,
                        left: `${X(r.startMs).toFixed(3)}%`, width: `${Math.max(0.25, w).toFixed(3)}%`, minWidth: 4,
                        background: C[r.cat] || C.browse,
                        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                        boxShadow: markLongest ? `0 0 0 2px ${t.c.surface}, 0 0 0 3.5px ${t.status.warning}` : undefined,
                      }}
                    >
                      {w > 6 && mins > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", opacity: 0.94 }}>{mins}m</span>
                      )}
                    </b>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Verdict chip for a chapter row. */
export function VerdictChip({ v }) {
  const t = useDashTheme();
  const look = v.level === "look";
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, flex: "none",
        fontSize: 10.5, fontWeight: 800, letterSpacing: "0.7px", textTransform: "uppercase",
        padding: "3px 9px", borderRadius: 999,
        color: look ? t.c.text : t.c.textTertiary,
        background: look ? `${t.status.warning}33` : t.c.surfaceMuted,
      }}
    >
      {look ? <AlertTriangle size={11} /> : <Check size={11} />}
      {v.label}
    </span>
  );
}
